/**
 * In-app sign-in screen (FR-2.11) — username + password, authenticated via SRP by the
 * hook. Presentational: local input + validation state, delegates the SRP call and
 * error mapping to {@link useAuth}. Links out to sign-up and forgot-password.
 */

import { useState } from 'react';
import { validatePassword, validateUsername } from './validation';

export interface SignInFormProps {
  busy: boolean;
  error: string | null;
  notice: string | null;
  /** Prefilled username (e.g. after a password reset). */
  initialUsername?: string;
  onSubmit: (username: string, password: string) => void;
  onForgot: () => void;
  onSignUp: () => void;
}

export function SignInForm({
  busy,
  error,
  notice,
  initialUsername = '',
  onSubmit,
  onForgot,
  onSignUp,
}: SignInFormProps): React.JSX.Element {
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const invalid = validateUsername(username) ?? validatePassword(password);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    setLocalError(null);
    onSubmit(username.trim(), password);
  };

  const shownError = localError ?? error;

  return (
    <form className="auth-form" data-testid="auth-signin" onSubmit={submit} noValidate>
      <h2 className="auth-form__title">Sign in</h2>
      {notice && (
        <div className="auth-form__notice" data-testid="auth-notice">
          {notice}
        </div>
      )}
      <input
        aria-label="Username"
        data-testid="auth-username"
        placeholder="Username"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        autoFocus
      />
      <input
        type="password"
        aria-label="Password"
        data-testid="auth-password"
        placeholder="Password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <button
        type="submit"
        className="btn btn--primary"
        data-testid="auth-submit"
        disabled={busy}
      >
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      {shownError && (
        <div className="error-text" data-testid="auth-error">
          {shownError}
        </div>
      )}
      <div className="auth-form__links">
        <button
          type="button"
          className="btn btn--link"
          data-testid="auth-goto-forgot"
          onClick={onForgot}
        >
          Forgot password?
        </button>
        <button
          type="button"
          className="btn btn--link"
          data-testid="auth-goto-signup"
          onClick={onSignUp}
        >
          Create an account
        </button>
      </div>
    </form>
  );
}
